import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Input, Label, Textarea } from "../ui/input";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Sparkles, Building2, Pencil, CheckCircle2, AlertTriangle, XCircle, ExternalLink, Scale, GraduationCap, ArrowRight, Lightbulb } from "lucide-react";
import { formatSgd, formatNum } from "../../lib/utils";
import { toast } from "sonner";
import GrantApplications from "./GrantApplications";
import GrantRefreshQueue from "./GrantRefreshQueue";

const ELIG_BADGE = { eligible: "success", likely_eligible: "warning", not_eligible: "destructive" };
const ELIG_LABEL = { eligible: "Eligible", likely_eligible: "Likely eligible", not_eligible: "Not eligible" };
const ELIG_ICON = { eligible: CheckCircle2, likely_eligible: AlertTriangle, not_eligible: XCircle };

function TriSelect({ value, onChange, testid }) {
  const v = value === true ? "yes" : value === false ? "no" : "unknown";
  return (
    <Select value={v} onValueChange={(x) => onChange(x === "yes" ? true : x === "no" ? false : null)}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="yes">Yes</SelectItem>
        <SelectItem value="no">No</SelectItem>
        <SelectItem value="unknown">Not sure yet</SelectItem>
      </SelectContent>
    </Select>
  );
}

function ProfileDialog({ open, onOpenChange, profile, initiativeTypes, onSaved }) {
  const [f, setF] = useState(profile || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(profile || {}); }, [open, profile]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f };
      delete body.id; delete body.tenant_id; delete body.updated_at;
      if (body.local_shareholding_pct !== null && body.local_shareholding_pct !== "") body.local_shareholding_pct = Number(body.local_shareholding_pct);
      if (body.group_revenue_sgd !== null && body.group_revenue_sgd !== "") body.group_revenue_sgd = Number(body.group_revenue_sgd);
      if (body.group_employees !== null && body.group_employees !== "") body.group_employees = Number(body.group_employees);
      await api.put("/grants/profile", body);
      toast.success("Grant profile updated");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const toggleInit = (v) => {
    const cur = f.planned_initiatives || [];
    set("planned_initiatives", cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="grant-profile-dialog">
        <DialogHeader><DialogTitle>Organisation grant profile</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>ACRA UEN</Label><Input value={f.uen || ""} onChange={(e) => set("uen", e.target.value)} data-testid="gp-uen" /></div>
          <div><Label>SSIC code</Label><Input value={f.ssic_code || ""} onChange={(e) => set("ssic_code", e.target.value)} data-testid="gp-ssic" /></div>
          <div><Label>Incorporation status</Label>
            <Select value={f.incorporation_status || "unknown"} onValueChange={(v) => set("incorporation_status", v === "unknown" ? null : v)}>
              <SelectTrigger data-testid="gp-incorporation"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="registered_sg">Registered & operating in Singapore</SelectItem>
                <SelectItem value="not_registered">Not registered in Singapore</SelectItem>
                <SelectItem value="unknown">Not sure</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Listing status</Label>
            <Select value={f.listing_status || "unknown"} onValueChange={(v) => set("listing_status", v === "unknown" ? null : v)}>
              <SelectTrigger data-testid="gp-listing"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sgx_listed">SGX-listed</SelectItem>
                <SelectItem value="non_listed">Non-listed</SelectItem>
                <SelectItem value="unknown">Not sure</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Local shareholding (%)</Label><Input type="number" value={f.local_shareholding_pct ?? ""} onChange={(e) => set("local_shareholding_pct", e.target.value === "" ? null : e.target.value)} data-testid="gp-shareholding" /></div>
          <div><Label>Group revenue (S$)</Label><Input type="number" value={f.group_revenue_sgd ?? ""} onChange={(e) => set("group_revenue_sgd", e.target.value === "" ? null : e.target.value)} data-testid="gp-revenue" /></div>
          <div><Label>Group employees</Label><Input type="number" value={f.group_employees ?? ""} onChange={(e) => set("group_employees", e.target.value === "" ? null : e.target.value)} data-testid="gp-employees" /></div>
          <div><Label>Financially viable for project</Label><TriSelect value={f.financially_viable} onChange={(v) => set("financially_viable", v)} testid="gp-viable" /></div>
          <div><Label>Equipment will be used in SG</Label><TriSelect value={f.equipment_in_sg} onChange={(v) => set("equipment_in_sg", v)} testid="gp-equipment" /></div>
          <div><Label>First ISSB-aligned report intent</Label><TriSelect value={f.first_issb_report_intent} onChange={(v) => set("first_issb_report_intent", v)} testid="gp-issb" /></div>
        </div>
        <div className="mt-2">
          <Label>Planned sustainability initiative types</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {initiativeTypes.map((t) => {
              const on = (f.planned_initiatives || []).includes(t.value);
              return (
                <button key={t.value} type="button" onClick={() => toggleInit(t.value)} data-testid={`gp-init-${t.value}`}
                  className={`px-3 py-1.5 rounded-sm border text-xs transition-colors ${on ? "border-accent bg-accent/10" : "border-border text-muted-foreground hover:bg-surface-hover"}`}>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="primary" onClick={save} disabled={busy} data-testid="gp-save">{busy ? "Saving…" : "Save profile"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MatchCard({ m, expanded, onToggle, compared, onCompare, onApply, hasOpenApp }) {
  const ev = m.evaluation || {};
  const Icon = ELIG_ICON[ev.status] || AlertTriangle;
  const fm = m.funding_mechanics || {};
  const navigate = useNavigate();
  return (
    <div className="card-flat p-5" data-testid={`grant-match-${m.code}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold">{m.name}</h3>
            <Badge variant={ELIG_BADGE[ev.status] || "outline"} className="text-[10px] uppercase" data-testid={`match-status-${m.code}`}>
              <Icon className="h-3 w-3 mr-1" />{ELIG_LABEL[ev.status] || ev.status}
            </Badge>
            <Badge variant="outline" className="text-[10px]">{m.agency}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{m.description}</p>
          <p className="text-xs mt-2" data-testid={`match-summary-${m.code}`}>{ev.summary}</p>
          <div className="flex items-center gap-4 mt-2 text-[11px] text-muted-foreground flex-wrap">
            {fm.co_funding_pct_sme != null && <span>Co-funding (SME): {fm.co_funding_pct_sme}%</span>}
            {fm.cap_sgd != null && <span>Cap: {formatSgd(fm.cap_sgd)}</span>}
            <span>Documents: {m.doc_burden ?? (m.required_documents || []).length}</span>
            {m.processing_time_weeks && <span>~{m.processing_time_weeks} weeks</span>}
            <span>Verified: {m.last_verified_date || "—"}</span>
            {m.source_url && <a href={m.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">Official source <ExternalLink className="h-3 w-3" /></a>}
          </div>
          {ev.status === "not_eligible" && (ev.alternatives || []).length > 0 && (
            <div className="mt-3 border border-accent/30 rounded-sm p-3 bg-accent/5" data-testid={`grant-alternatives-${m.code}`}>
              <div className="label-eyebrow text-[10px] mb-1.5 inline-flex items-center gap-1"><Lightbulb className="h-3 w-3 text-accent" /> Recommended next steps</div>
              <div className="space-y-2">
                {ev.alternatives.map((a, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-3 flex-wrap" data-testid={`grant-alt-${m.code}-${idx}`}>
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-xs font-medium">{a.label}</div>
                      <div className="text-[11px] text-muted-foreground">{a.detail}</div>
                    </div>
                    {a.route ? (
                      <Button size="sm" variant="outline" onClick={() => navigate(a.route)} data-testid={`grant-alt-action-${m.code}-${idx}`}>
                        {a.action_label} <ArrowRight className="h-3 w-3" />
                      </Button>
                    ) : (
                      <a href={a.url} target="_blank" rel="noreferrer" data-testid={`grant-alt-action-${m.code}-${idx}`}>
                        <Button size="sm" variant="outline">{a.action_label} <ExternalLink className="h-3 w-3" /></Button>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="font-display text-2xl font-bold">{ev.score}<span className="text-xs text-muted-foreground font-sans">/100</span></div>
          <div className="flex flex-col gap-1.5 mt-2">
            <Button size="sm" variant="primary" onClick={() => onApply(m)} disabled={hasOpenApp || ev.status === "not_eligible"}
              title={ev.status === "not_eligible" ? ev.summary : undefined} data-testid={`start-application-${m.code}`}>
              {hasOpenApp ? "Application open" : ev.status === "not_eligible" ? "Not eligible" : "Start application"}
            </Button>
            {ev.status === "not_eligible" && <div className="text-[10px] text-red-400 max-w-[180px] text-right" data-testid={`inelig-reason-${m.code}`}>{ev.summary}</div>}
            <div className="flex gap-1.5 justify-end">
              <Button size="sm" variant="outline" onClick={onToggle} data-testid={`toggle-criteria-${m.code}`}>{expanded ? "Hide criteria" : "Criteria"}</Button>
              <Button size="sm" variant={compared ? "primary" : "outline"} onClick={onCompare} data-testid={`compare-${m.code}`}><Scale className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 border-t border-border pt-3 space-y-1.5" data-testid={`criteria-list-${m.code}`}>
          {(ev.criteria || []).map((c) => (
            <div key={c.key} className="flex items-start gap-2 text-xs">
              {c.result === "pass" ? <CheckCircle2 className="h-3.5 w-3.5 text-[#00E65B] mt-0.5 shrink-0" />
                : c.result === "fail" ? <XCircle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
                : <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />}
              <div>
                <span>{c.label}</span>
                {c.result !== "pass" && c.verify_hint && <span className="text-muted-foreground"> — {c.verify_hint}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GrantsTab() {
  const [subtab, setSubtab] = useState("discover");
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [reference, setReference] = useState({ initiative_types: [], app_statuses: [], draft_types: [] });
  const [apps, setApps] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [compare, setCompare] = useState([]);
  const [goals, setGoals] = useState("");
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [intakeRes, setIntakeRes] = useState(null);

  const load = async () => {
    try {
      const [r, p, m, c, a] = await Promise.all([
        api.get("/grants/reference"), api.get("/grants/profile"), api.get("/grants/matches"),
        api.get("/grants/catalogue"), api.get("/grants/applications"),
      ]);
      setReference(r.data); setProfile(p.data); setMatches(m.data.matches);
      setDisclaimer(m.data.disclaimer); setProgrammes(c.data.programmes); setApps(a.data.applications);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openAppCodes = new Set(apps.filter((a) => !["completed", "withdrawn"].includes(a.status)).map((a) => a.grant?.code));
  const eligibleCount = matches.filter((m) => m.evaluation?.status === "eligible").length;
  const likelyCount = matches.filter((m) => m.evaluation?.status === "likely_eligible").length;

  const runIntake = async () => {
    if (goals.trim().length < 10) { toast.error("Describe your goals in a little more detail"); return; }
    setIntakeBusy(true);
    try { const { data } = await api.post("/grants/intake", { goals_text: goals }); setIntakeRes(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setIntakeBusy(false); }
  };

  const startApplication = async (m) => {
    try {
      await api.post("/grants/applications", { grant_id: m.id });
      toast.success(`Application case created for ${m.code}`);
      setSubtab("applications"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const comparedGrants = matches.filter((m) => compare.includes(m.code));

  return (
    <div className="space-y-5" data-testid="grants-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card-flat p-4" data-testid="grants-stat-eligible"><div className="label-eyebrow mb-1">Eligible matches</div><div className="font-display text-2xl font-bold text-[#00E65B]">{eligibleCount}</div></div>
        <div className="card-flat p-4" data-testid="grants-stat-likely"><div className="label-eyebrow mb-1">Likely eligible</div><div className="font-display text-2xl font-bold text-amber-400">{likelyCount}</div></div>
        <div className="card-flat p-4" data-testid="grants-stat-apps"><div className="label-eyebrow mb-1">Open applications</div><div className="font-display text-2xl font-bold">{apps.filter((a) => !["completed", "withdrawn"].includes(a.status)).length}</div></div>
        <div className="card-flat p-4" data-testid="grants-stat-programmes"><div className="label-eyebrow mb-1">Capability programmes</div><div className="font-display text-2xl font-bold">{programmes.length}</div></div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant={subtab === "discover" ? "primary" : "outline"} onClick={() => setSubtab("discover")} data-testid="grants-subtab-discover">Discover & Match</Button>
        <Button size="sm" variant={subtab === "applications" ? "primary" : "outline"} onClick={() => setSubtab("applications")} data-testid="grants-subtab-applications">Applications ({apps.length})</Button>
        <Button size="sm" variant={subtab === "refresh" ? "primary" : "outline"} onClick={() => setSubtab("refresh")} data-testid="grants-subtab-refresh">Catalogue Refresh</Button>
      </div>

      {subtab === "discover" && (
        <>
          <div className="card-flat p-5" data-testid="grant-profile-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-accent" /><h3 className="font-display font-semibold">Organisation grant profile</h3></div>
              <Button size="sm" variant="outline" onClick={() => setProfileOpen(true)} data-testid="edit-grant-profile"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit profile</Button>
            </div>
            {profile && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 mt-3 text-xs">
                <div><span className="text-muted-foreground">UEN:</span> {profile.uen || "—"}</div>
                <div><span className="text-muted-foreground">Local shareholding:</span> {profile.local_shareholding_pct != null ? `${profile.local_shareholding_pct}%` : "—"}</div>
                <div><span className="text-muted-foreground">Group revenue:</span> {profile.group_revenue_sgd != null ? formatSgd(profile.group_revenue_sgd) : "—"}</div>
                <div><span className="text-muted-foreground">Employees:</span> {profile.group_employees != null ? formatNum(profile.group_employees) : "—"}</div>
                <div><span className="text-muted-foreground">Listing:</span> {profile.listing_status === "sgx_listed" ? "SGX-listed" : profile.listing_status === "non_listed" ? "Non-listed" : "—"}</div>
                <div><span className="text-muted-foreground">SSIC:</span> {profile.ssic_code || "—"}</div>
                <div><span className="text-muted-foreground">ISSB report intent:</span> {profile.first_issb_report_intent === true ? "Yes" : profile.first_issb_report_intent === false ? "No" : "—"}</div>
                <div><span className="text-muted-foreground">Initiatives:</span> {(profile.planned_initiatives || []).length || "—"}</div>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-2">Fields marked — reduce match confidence: complete them to turn "Likely eligible" into a firm result.</p>
          </div>

          <div className="card-flat p-5" data-testid="grant-intake-card">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-accent" /><h3 className="font-display font-semibold">AI grant discovery</h3></div>
            <p className="text-xs text-muted-foreground mb-2">Describe your sustainability goals in plain language — the assistant maps them to relevant schemes, then the rules engine confirms eligibility.</p>
            <Textarea rows={3} value={goals} onChange={(e) => setGoals(e.target.value)} data-testid="intake-goals"
              placeholder="e.g. We want to replace our old chillers with energy-efficient units and prepare our first sustainability report…" />
            <div className="flex justify-end mt-2">
              <Button size="sm" variant="primary" onClick={runIntake} disabled={intakeBusy} data-testid="intake-run"><Sparkles className="h-3.5 w-3.5 mr-1" />{intakeBusy ? "Analysing…" : "Find matching grants"}</Button>
            </div>
            {intakeRes && (
              <div className="mt-3 space-y-2" data-testid="intake-results">
                {intakeRes.recommendations.map((r) => (
                  <div key={r.grant.code} className="border border-border rounded-sm p-3 text-xs flex items-start gap-3">
                    <Badge variant={ELIG_BADGE[r.evaluation.status] || "outline"} className="text-[9px] uppercase shrink-0">{ELIG_LABEL[r.evaluation.status]}</Badge>
                    <div><span className="font-semibold">{r.grant.name}</span> — {r.reason}
                      <div className="text-muted-foreground mt-0.5">{r.evaluation.summary}</div>
                    </div>
                  </div>
                ))}
                {intakeRes.note && <p className="text-[11px] text-muted-foreground">{intakeRes.note}</p>}
              </div>
            )}
          </div>

          <div className="space-y-3">
            {matches.map((m) => (
              <MatchCard key={m.code} m={m}
                expanded={!!expanded[m.code]} onToggle={() => setExpanded((p) => ({ ...p, [m.code]: !p[m.code] }))}
                compared={compare.includes(m.code)}
                onCompare={() => setCompare((p) => (p.includes(m.code) ? p.filter((x) => x !== m.code) : [...p, m.code]))}
                onApply={startApplication} hasOpenApp={openAppCodes.has(m.code)} />
            ))}
          </div>

          {comparedGrants.length >= 2 && (
            <div className="card-flat p-5 overflow-x-auto" data-testid="grant-comparison">
              <h3 className="font-display font-semibold mb-3">Side-by-side comparison</h3>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4">Criteria</th>
                  {comparedGrants.map((g) => <th key={g.code} className="py-2 pr-4">{g.code}</th>)}
                </tr></thead>
                <tbody>
                  {[["Agency", (g) => g.agency],
                    ["Eligibility", (g) => ELIG_LABEL[g.evaluation?.status] || "—"],
                    ["Co-funding (SME)", (g) => g.funding_mechanics?.co_funding_pct_sme != null ? `${g.funding_mechanics.co_funding_pct_sme}%` : "—"],
                    ["Funding cap", (g) => g.funding_mechanics?.cap_sgd != null ? formatSgd(g.funding_mechanics.cap_sgd) : "—"],
                    ["Document burden", (g) => `${g.doc_burden ?? (g.required_documents || []).length} documents`],
                    ["Processing time", (g) => g.processing_time_weeks ? `~${g.processing_time_weeks} weeks` : "—"],
                    ["Channel", (g) => g.application_channel?.name || "—"]].map(([label, fn]) => (
                    <tr key={label} className="border-b border-border/50">
                      <td className="py-2 pr-4 text-muted-foreground">{label}</td>
                      {comparedGrants.map((g) => <td key={g.code} className="py-2 pr-4">{fn(g)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {programmes.length > 0 && (
            <div data-testid="capability-programmes">
              <div className="flex items-center gap-2 mb-2"><GraduationCap className="h-4 w-4 text-accent" /><h3 className="font-display font-semibold">Capability-building programmes</h3></div>
              <div className="grid md:grid-cols-3 gap-3">
                {programmes.map((p) => (
                  <div key={p.code} className="card-flat p-4" data-testid={`programme-${p.code}`}>
                    <div className="text-sm font-semibold">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground">{p.agency}</div>
                    <p className="text-xs text-muted-foreground mt-1">{p.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground border border-border rounded-sm p-3" data-testid="grants-disclaimer">{disclaimer}</p>
        </>
      )}

      {subtab === "applications" && (
        <GrantApplications apps={apps} reference={reference} onChanged={load} />
      )}

      {subtab === "refresh" && (
        <GrantRefreshQueue grants={matches} onCatalogueChanged={load} />
      )}

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} profile={profile}
        initiativeTypes={reference.initiative_types || []} onSaved={load} />
    </div>
  );
}
